import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Fuel, DollarSign, MapPin, CreditCard, TrendingUp,
  CheckCircle, Truck, Calculator, Clock, Building,
  Shield, FileText, Smartphone, ArrowRight, Zap
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FuelCardPage = () => {
  const [activeTab, setActiveTab] = useState('overview'); // overview, apply, dashboard, stations, calculator
  const [carrierEmail, setCarrierEmail] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [programInfo, setProgramInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Application form
  const [appForm, setAppForm] = useState({
    carrierName: '',
    companyName: '',
    mcNumber: '',
    dotNumber: '',
    fleetSize: '1',
    monthlyGallons: '1000',
    billingAddress: '',
    phone: ''
  });

  // Calculator
  const [calcGallons, setCalcGallons] = useState('1000');
  const [calcPrice, setCalcPrice] = useState('3.50');
  const [calcResult, setCalcResult] = useState(null);

  // Stations
  const [stations, setStations] = useState([]);

  useEffect(() => {
    fetchProgramInfo();
  }, []);

  const fetchProgramInfo = async () => {
    try {
      const res = await axios.get(`${API}/fuelcard/program-info`);
      setProgramInfo(res.data);
    } catch (error) {
      console.error('Error fetching program info:', error);
    }
  };

  const handleLogin = async () => {
    if (!carrierEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/fuelcard/carrier/${encodeURIComponent(carrierEmail)}`);
      setCardData(res.data);
      setIsLoggedIn(true);
      
      if (res.data.has_card) {
        setActiveTab('dashboard');
        toast.success('Welcome back!');
      } else {
        setActiveTab('apply');
        toast.info('Apply for your fuel card to start saving!');
      }
    } catch (error) {
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!appForm.carrierName || !appForm.monthlyGallons) {
      toast.error('Please fill required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/fuelcard/apply`, {
        carrier_email: carrierEmail,
        carrier_name: appForm.carrierName,
        company_name: appForm.companyName || appForm.carrierName,
        mc_number: appForm.mcNumber,
        dot_number: appForm.dotNumber,
        fleet_size: parseInt(appForm.fleetSize) || 1,
        estimated_monthly_gallons: parseInt(appForm.monthlyGallons) || 1000,
        billing_address: appForm.billingAddress,
        phone: appForm.phone
      });

      toast.success(res.data.message);
      
      // Refresh card data
      const cardRes = await axios.get(`${API}/fuelcard/carrier/${encodeURIComponent(carrierEmail)}`);
      setCardData(cardRes.data);
      setActiveTab('dashboard');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API}/fuelcard/stations?limit=20`);
      setStations(res.data.stations || []);
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const calculateSavings = async () => {
    try {
      const res = await axios.get(`${API}/fuelcard/savings-calculator`, {
        params: {
          monthly_gallons: parseInt(calcGallons),
          current_price: parseFloat(calcPrice)
        }
      });
      setCalcResult(res.data);
    } catch (error) {
      toast.error('Calculation error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6 max-w-7xl mx-auto" data-testid="fuelcard-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Fuel className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Tempus Fuel Card</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Save up to $0.15/gallon at 50,000+ locations. No fees. No hassle.
          </p>
        </div>

        {/* Login Section */}
        {!isLoggedIn ? (
          <div className="max-w-md mx-auto mb-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-orange-500" />
                  Access Your Fuel Card
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  type="email"
                  value={carrierEmail}
                  onChange={(e) => setCarrierEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  data-testid="fuelcard-email-input"
                />
                <Button 
                  onClick={handleLogin}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500"
                  data-testid="fuelcard-login-btn"
                >
                  {loading ? 'Loading...' : 'Continue'}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-6 justify-center">
              {['overview', cardData?.has_card ? 'dashboard' : 'apply', 'stations', 'calculator'].map((tab) => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? 'default' : 'outline'}
                  onClick={() => {
                    setActiveTab(tab);
                    if (tab === 'stations') fetchStations();
                  }}
                  className={activeTab === tab ? 'bg-orange-500' : ''}
                >
                  {tab === 'overview' && <FileText className="w-4 h-4 mr-2" />}
                  {tab === 'apply' && <CreditCard className="w-4 h-4 mr-2" />}
                  {tab === 'dashboard' && <TrendingUp className="w-4 h-4 mr-2" />}
                  {tab === 'stations' && <MapPin className="w-4 h-4 mr-2" />}
                  {tab === 'calculator' && <Calculator className="w-4 h-4 mr-2" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Button>
              ))}
            </div>
          </>
        )}

        {/* Overview Tab */}
        {(activeTab === 'overview' || !isLoggedIn) && programInfo && (
          <div className="space-y-8">
            {/* Hero Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/30">
                <CardContent className="p-6 text-center">
                  <DollarSign className="w-10 h-10 text-orange-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white">{programInfo.average_discount}</p>
                  <p className="text-orange-300">Savings Per Gallon</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
                <CardContent className="p-6 text-center">
                  <MapPin className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white">{programInfo.partner_network}</p>
                  <p className="text-blue-300">Partner Locations</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white">{programInfo.annual_savings_example}</p>
                  <p className="text-green-300">Annual Savings*</p>
                </CardContent>
              </Card>
            </div>

            {/* Features */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-500" />
                  Program Benefits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {programInfo.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Partner Stations */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building className="w-5 h-5 text-blue-500" />
                  Partner Network
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {programInfo.partner_stations?.map((partner, idx) => (
                    <div key={idx} className="p-4 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{partner.name}</span>
                        <span className="text-green-400 text-sm">-${partner.discount}/gal</span>
                      </div>
                      <p className="text-gray-400 text-sm">{partner.locations}+ locations</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            {!isLoggedIn && (
              <div className="text-center">
                <p className="text-gray-400 mb-4">Ready to start saving?</p>
                <Button 
                  onClick={() => document.querySelector('[data-testid="fuelcard-email-input"]')?.focus()}
                  className="bg-gradient-to-r from-orange-500 to-red-500 px-8 py-6 text-lg"
                >
                  <CreditCard className="w-5 h-5 mr-2" /> Get Your Fuel Card
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Apply Tab */}
        {activeTab === 'apply' && isLoggedIn && (
          <div className="max-w-2xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-orange-500" />
                  Apply for Tempus Fuel Card
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Your Name *</label>
                    <Input
                      value={appForm.carrierName}
                      onChange={(e) => setAppForm({...appForm, carrierName: e.target.value})}
                      placeholder="John Smith"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Company Name</label>
                    <Input
                      value={appForm.companyName}
                      onChange={(e) => setAppForm({...appForm, companyName: e.target.value})}
                      placeholder="Smith Trucking LLC"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">MC Number</label>
                    <Input
                      value={appForm.mcNumber}
                      onChange={(e) => setAppForm({...appForm, mcNumber: e.target.value})}
                      placeholder="MC-123456"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">DOT Number</label>
                    <Input
                      value={appForm.dotNumber}
                      onChange={(e) => setAppForm({...appForm, dotNumber: e.target.value})}
                      placeholder="DOT-123456"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Fleet Size (# of trucks)</label>
                    <Input
                      type="number"
                      value={appForm.fleetSize}
                      onChange={(e) => setAppForm({...appForm, fleetSize: e.target.value})}
                      placeholder="1"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Est. Monthly Gallons *</label>
                    <Input
                      type="number"
                      value={appForm.monthlyGallons}
                      onChange={(e) => setAppForm({...appForm, monthlyGallons: e.target.value})}
                      placeholder="1000"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Phone Number</label>
                  <Input
                    value={appForm.phone}
                    onChange={(e) => setAppForm({...appForm, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Billing Address</label>
                  <Input
                    value={appForm.billingAddress}
                    onChange={(e) => setAppForm({...appForm, billingAddress: e.target.value})}
                    placeholder="123 Main St, Dallas, TX 75001"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                {/* Estimated Savings Preview */}
                {appForm.monthlyGallons && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 font-medium mb-2">Your Estimated Savings:</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Monthly:</span>
                      <span className="text-white font-medium">{formatCurrency(parseInt(appForm.monthlyGallons) * 0.15)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Annual:</span>
                      <span className="text-green-400 font-bold">{formatCurrency(parseInt(appForm.monthlyGallons) * 0.15 * 12)}</span>
                    </div>
                  </div>
                )}

                <Button 
                  onClick={handleApply}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 py-6"
                  data-testid="apply-fuelcard-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By applying, you agree to our terms of service. Card delivery takes 5-7 business days.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && isLoggedIn && cardData?.has_card && (
          <div className="space-y-6">
            {/* Card Status */}
            <Card className={`border-2 ${
              cardData.card?.status === 'active' 
                ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/50' 
                : 'bg-slate-800/50 border-slate-700'
            }`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Tempus Fuel Card</p>
                      <p className="text-2xl font-mono text-white">{cardData.card?.card_number || 'Pending'}</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        cardData.card?.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        cardData.card?.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {cardData.card?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-sm">Total Savings</p>
                    <p className="text-3xl font-bold text-green-400">{formatCurrency(cardData.stats?.total_savings)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid sm:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <Fuel className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{cardData.stats?.total_gallons?.toLocaleString() || 0}</p>
                  <p className="text-sm text-gray-400">Gallons Pumped</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <DollarSign className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{formatCurrency(cardData.stats?.total_spent)}</p>
                  <p className="text-sm text-gray-400">Total Spent</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{formatCurrency(cardData.stats?.total_savings)}</p>
                  <p className="text-sm text-gray-400">Total Saved</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <FileText className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{cardData.stats?.transaction_count || 0}</p>
                  <p className="text-sm text-gray-400">Transactions</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Transactions */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Recent Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {cardData.recent_transactions?.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No transactions yet. Start fueling to see your history!</p>
                ) : (
                  <div className="space-y-3">
                    {cardData.recent_transactions?.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Fuel className="w-8 h-8 text-orange-400" />
                          <div>
                            <p className="text-white font-medium">{tx.station_name}</p>
                            <p className="text-gray-400 text-sm">{tx.gallons} gallons @ ${tx.price_per_gallon}/gal</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white">{formatCurrency(tx.discounted_total)}</p>
                          <p className="text-green-400 text-sm">Saved {formatCurrency(tx.savings)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stations Tab */}
        {activeTab === 'stations' && isLoggedIn && (
          <div className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  Partner Fuel Stations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stations.length === 0 ? (
                    <p className="text-gray-400 col-span-full text-center py-8">Loading stations...</p>
                  ) : (
                    stations.map((station) => (
                      <div key={station.id} className="p-4 bg-slate-700/30 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-white font-medium">{station.name}</p>
                            <p className="text-gray-400 text-sm">{station.brand}</p>
                          </div>
                          <span className="text-green-400 text-sm font-medium">
                            -${station.discount_per_gallon}/gal
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm mb-2">{station.address}, {station.city}, {station.state}</p>
                        <div className="flex flex-wrap gap-1">
                          {station.amenities?.map((amenity, idx) => (
                            <span key={idx} className="text-xs bg-slate-600 text-gray-300 px-2 py-0.5 rounded">
                              {amenity}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === 'calculator' && isLoggedIn && (
          <div className="max-w-xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-orange-500" />
                  Savings Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Monthly Gallons</label>
                  <Input
                    type="number"
                    value={calcGallons}
                    onChange={(e) => setCalcGallons(e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Current Price Per Gallon ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={calcPrice}
                    onChange={(e) => setCalcPrice(e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <Button onClick={calculateSavings} className="w-full bg-orange-500">
                  Calculate My Savings
                </Button>

                {calcResult && (
                  <div className="mt-6 space-y-4">
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                      <p className="text-green-400 text-sm mb-1">Your Annual Savings</p>
                      <p className="text-4xl font-bold text-white">{formatCurrency(calcResult.savings?.annual)}</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <p className="text-gray-400 text-xs">Per Gallon</p>
                        <p className="text-white font-bold">${calcResult.savings?.per_gallon}</p>
                      </div>
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <p className="text-gray-400 text-xs">Monthly</p>
                        <p className="text-white font-bold">{formatCurrency(calcResult.savings?.monthly)}</p>
                      </div>
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <p className="text-gray-400 text-xs">5 Year</p>
                        <p className="text-green-400 font-bold">{formatCurrency(calcResult.savings?.five_year)}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-slate-700/30 rounded-lg">
                      <p className="text-gray-400 text-sm mb-2">Monthly Comparison</p>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-300">Without Card:</span>
                        <span className="text-red-400">{formatCurrency(calcResult.comparison?.monthly_without_card)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">With Tempus Card:</span>
                        <span className="text-green-400">{formatCurrency(calcResult.comparison?.monthly_with_card)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Back to Login */}
        {isLoggedIn && (
          <div className="text-center mt-8">
            <Button 
              variant="ghost" 
              onClick={() => {
                setIsLoggedIn(false);
                setCardData(null);
                setActiveTab('overview');
              }}
              className="text-gray-400"
            >
              Switch Account
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FuelCardPage;
